import { onKeyStroke } from '@vueuse/core';
import type { Ref } from 'vue';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * A11y de modal — Escape fecha, Tab fica preso dentro do container, foco vai
 * pro primeiro elemento focável ao abrir e volta pro que tinha foco antes ao
 * fechar (Handoff: "role=dialog, aria-modal, foco preso e retorno de foco ao
 * fechar"). Extraído de components/base/modal/Modal.vue pra ser reusado por
 * modais com shell próprio (ex.: BonusRules.vue) sem duplicar a lógica.
 *
 * `container` é o elemento com role="dialog" (o consumidor cabeia isso no
 * template); `isOpen` decide quando o foco entra/sai; `onEscape` roda só com
 * o modal aberto — quem chama decide se fecha ou ignora (ex.: variante
 * persistent do BaseModal).
 */
export default function useModalA11y(
  container: Ref<HTMLElement | null>,
  isOpen: Ref<boolean>,
  onEscape: () => void,
) {
  onKeyStroke('Escape', () => {
    if (isOpen.value) onEscape();
  });

  function getFocusableElements(): HTMLElement[] {
    if (!container.value) return [];

    return Array.from(
      container.value.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter((el) => el.offsetParent !== null);
  }

  function trapFocus(event: KeyboardEvent) {
    if (event.key !== 'Tab') return;

    const focusable = getFocusableElements();

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  let elementFocusedBeforeOpen: HTMLElement | null = null;

  watch(
    isOpen,
    async (open) => {
      if (open) {
        elementFocusedBeforeOpen = document.activeElement as HTMLElement;

        await nextTick();

        const target =
          getFocusableElements().find(
            (el) => !el.hasAttribute('data-autofocus-skip'),
          ) ?? container.value;

        target?.focus();
      } else {
        elementFocusedBeforeOpen?.focus();
        elementFocusedBeforeOpen = null;
      }
    },
    { immediate: true },
  );

  return { trapFocus };
}
